/**
 * Районные циклы, культуры и дата среза — из `GET /agrocycles` (строки
 * агроциклов, ядро S4; прежняя сборка из `/season/cycles` + шаблонов снята).
 *
 * Прототип держал три набора (`cycles.json`, `crops.json`, `meta.json`), канон
 * отвечает на них ОДНИМ обходом. Поэтому все три собраны здесь, а не разложены
 * по «доменам» имён файлов: домен здесь — не файл прототипа, а серверный
 * ответ, из которого набор вынимается. Разложив их по трём адаптерам, мы
 * получили бы три одинаковых запроса на один экран.
 *
 * Что здесь меняется по смыслу и как это сказано:
 *
 * * **цикл — строка сервера.** Ключ `2026:COTTON` разрешается в строку
 *   `work_agrocycle`; шаблон, участники (по размещению), этап и счёт плана
 *   приезжают с ней — подбирать шаблон в браузере больше не нужно;
 * * **состояний у строки три, у прототипа два.** `not_started` — план ещё
 *   проекция шаблона. Прототип такого ярлыка не знает: неначатый цикл
 *   попадает в незакрытые с ПУСТЫМ текущим этапом, а не с придуманным;
 * * **чего канон не считает, того здесь нет.** «Работ выполнено» и «работ
 *   идёт» сервер по циклу не отдаёт (это разрез кампаний, а не цикла), и поля
 *   остаются незаполненными — экран напечатает «—», а не ноль.
 */

import { api, type QueryParams, type Schemas } from '../../../api/client';
import type { Crop, Cycle, Meta } from '../../data/types';
import { canonCrops } from './crops';
import { type DatasetAdapter, once, paramsKey } from './adapter';

type AgrocycleRow = Schemas['AgrocycleOut'];

export const CYCLES_SET = 'cycles.json';
export const CROPS_SET = 'crops.json';
export const META_SET = 'meta.json';

async function bundle(params: QueryParams): Promise<Schemas['AgrocyclesOut']> {
  return once(`cycles|${paramsKey(params)}`, () => api.agrocycles(params));
}

function kpi(row: AgrocycleRow, id: string): number | undefined {
  const value = row.kpis?.[id]?.value;
  return typeof value === 'number' ? value : undefined;
}

/**
 * Строка агроцикла сервера → карточка цикла каркаса (ядро S4).
 *
 * Цикл — СТРОКА `work_agrocycle` (ключ `2026:COTTON`), а не пара, собранная
 * обходом кампаний и выбором шаблона в браузере: шаблон, участники, земля,
 * этап и счёт плана приезжают с сервера (слой метрик). Чего сервер не
 * считает — остаётся незаполненным, экран печатает «—», а не ноль.
 */
export function toCycle(row: AgrocycleRow): Cycle {
  return {
    id: row.key,
    template_id: String(row.template_id),
    crop: row.crop_code,
    season_label: row.season_label,
    // Каркас знает «закрыт / идёт»: неначатый цикл стоит среди незакрытых
    // с пустым текущим этапом, а не с придуманным.
    status: row.status === 'finished' ? 'closed' : 'active',
    window: { from: row.plan_from ?? '', to: row.plan_to ?? '' },
    farms: row.participants.farms,
    contours: row.participants.fields,
    area_ha: (row.participants.land_ha ?? undefined) as number,
    stage_now: row.status === 'not_started' ? null : (row.current_stage?.name ?? null),
    progress: {
      works_total: (kpi(row, 'plan.works') ?? undefined) as number,
      works_done: undefined as unknown as number,
      works_active: undefined as unknown as number,
      reported_pct: kpi(row, 'reports.done_pct') as number,
      verified_pct: undefined as unknown as number,
    },
  };
}

/**
 * Справочник культур из тех же строк.
 *
 * Отдельного справочника канон не отдаёт, но каждая строка цикла несёт код и
 * имя своей культуры — этого экрану и хватает. Цвет НЕ приходит с сервера: это
 * решение оформления, и токен собирается по коду ровно так, как он назван в
 * теме Astra (`crop-cotton`). Культура, для которой токена нет, получит
 * запасной цвет темы — не показать её вовсе было бы хуже.
 */
export function toCrops(rows: readonly AgrocycleRow[]): Crop[] {
  // Весь словарь канона, а не только культуры циклов: карта красит и по
  // размещениям, у которых своего цикла нет (кормовые, картофель, прочие).
  const named = new Map<string, string>();
  for (const row of rows) if (!named.has(row.crop_code)) named.set(row.crop_code, row.crop_name);
  return canonCrops(named);
}

/**
 * Дата среза. Остальных полей `meta.json` канон не знает.
 *
 * Итогов района (массивы, хозяйства, площадь) сводка циклов не считает, а
 * подставить сюда суммы по циклам нельзя: хозяйство, ведущее два цикла, вошло
 * бы в район дважды. Поля остаются незаполненными — экран печатает своё
 * запасное имя района и «—» вместо чисел, а не выдуманную сумму.
 */
export function toMeta(horizon: string): Meta {
  return { as_of: horizon } as unknown as Meta;
}

export const cyclesAdapter: DatasetAdapter = {
  domain: 'cycles',

  match(name) {
    return name === CYCLES_SET || name === CROPS_SET || name === META_SET;
  },

  async load(name, params) {
    const data = await bundle(params);
    if (name === META_SET) return toMeta(data.horizon);
    if (name === CROPS_SET) return toCrops(data.items);
    return data.items.map(toCycle);
  },
};
